import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Brain, BrainCog, Database, ChevronDown, Info, Loader2, FileText, CornerDownLeft, Globe } from "lucide-react";
import {
  createAIProfile,
  updateAIProfile,
  fetchAIProfileByTask,
} from "../../../Api/aiProfileApi";
import {
  selectAIProfile,
  setAIProfile,
  setLoading,
  setError,
  clearAIProfile,
} from "../../../slice/aiProfileSlice";
import { selectSelectedTask } from "../../../slice/taskSlice";
import { useThemeColor } from "../../../hooks/useThemeColor";
import { useToast } from "../../../components/Toast";
import { useTranslation } from "../../../i18n/LanguageContext";

// Each Knowledge Source mode stores and saves its own prompt independently —
// switching modes must never show/overwrite another mode's saved content.
const PROMPT_FIELD_BY_MODE = {
  documents: "system_prompt_documents",
  web: "system_prompt_web",
  hybrid: "system_prompt_hybrid",
};

// Same per-mode independence for the Max Output Tokens safety cap — Web and
// Hybrid each get their own value instead of one shared setting. Documents
// mode has no entry here: it's governed solely by Response Length Limit
// (max_completion_tokens) and the card itself is hidden for that mode.
const MAX_OUTPUT_TOKENS_FIELD_BY_MODE = {
  web: "max_output_tokens_web",
  hybrid: "max_output_tokens_hybrid",
};

// Same per-mode independence for the Regex Detection debug toggle.
const REGEX_DETECTION_FIELD_BY_MODE = {
  documents: "regex_detection_documents",
  web: "regex_detection_web",
  hybrid: "regex_detection_hybrid",
};

// Vector K only applies to modes that actually do document retrieval —
// opposite of the web-only maps below (documents/hybrid, not web).
const VECTOR_K_FIELD_BY_MODE = { documents: "vector_k_documents", hybrid: "vector_k_hybrid" };
const USE_CUSTOM_VECTOR_K_FIELD_BY_MODE = { documents: "use_custom_vector_k_documents", hybrid: "use_custom_vector_k_hybrid" };

// model/reasoning_effort/qa_pairs apply to all three modes — each admin-
// visible per mode instead of one shared value editing whatever tab happens
// to be open. top_k/search_type only apply to modes with document retrieval.
const MODEL_FIELD_BY_MODE = { documents: "model_documents", web: "model_web", hybrid: "model_hybrid" };
const REASONING_EFFORT_FIELD_BY_MODE = { documents: "reasoning_effort_documents", web: "reasoning_effort_web", hybrid: "reasoning_effort_hybrid" };
const QA_PAIRS_FIELD_BY_MODE = { documents: "qa_pairs_documents", web: "qa_pairs_web", hybrid: "qa_pairs_hybrid" };
const TOP_K_FIELD_BY_MODE = { documents: "top_k_documents", hybrid: "top_k_hybrid" };
const USE_CUSTOM_TOP_K_FIELD_BY_MODE = { documents: "use_custom_top_k_documents", hybrid: "use_custom_top_k_hybrid" };
const SEARCH_TYPE_FIELD_BY_MODE = { documents: "search_type_documents", hybrid: "search_type_hybrid" };

// Search Context Size only applies to modes that actually do a web search —
// controls how much web content Azure uses to ground the answer (low/medium/
// high), not the number of cited sources. Replaces the old Max Web Results
// field, which only ever worked as an unreliable "cite at most N" prompt hint.
const SEARCH_CONTEXT_SIZE_FIELD_BY_MODE = {
  web: "search_context_size_web",
  hybrid: "search_context_size_hybrid",
};

// Custom Q&A pairs (chat history) toggle for Web Search — also only applies
// to modes that do a web search. Off by default: including history previously
// degraded web search answer quality.
const USE_WEB_CHAT_HISTORY_FIELD_BY_MODE = {
  web: "use_web_chat_history_web",
  hybrid: "use_web_chat_history_hybrid",
};

// Remaining Azure Responses API web-search parameters — all opt-in per mode,
// same pattern as the two maps above. Off/empty means the key is omitted
// entirely from the request instead of being sent with a default value.
const FORCE_WEB_SEARCH_FIELD_BY_MODE = { web: "force_web_search_web", hybrid: "force_web_search_hybrid" };
const STREAM_RESPONSE_FIELD_BY_MODE = { web: "stream_response_web", hybrid: "stream_response_hybrid" };
const MAX_TOOL_CALLS_FIELD_BY_MODE = { web: "max_tool_calls_web", hybrid: "max_tool_calls_hybrid" };
const ALLOWED_DOMAINS_FIELD_BY_MODE = { web: "allowed_domains_web", hybrid: "allowed_domains_hybrid" };
const BLOCKED_DOMAINS_FIELD_BY_MODE = { web: "blocked_domains_web", hybrid: "blocked_domains_hybrid" };
const USER_LOCATION_ENABLED_FIELD_BY_MODE = { web: "user_location_enabled_web", hybrid: "user_location_enabled_hybrid" };
const USER_LOCATION_COUNTRY_FIELD_BY_MODE = { web: "user_location_country_web", hybrid: "user_location_country_hybrid" };
const USER_LOCATION_CITY_FIELD_BY_MODE = { web: "user_location_city_web", hybrid: "user_location_city_hybrid" };
const USER_LOCATION_REGION_FIELD_BY_MODE = { web: "user_location_region_web", hybrid: "user_location_region_hybrid" };
const USER_LOCATION_TIMEZONE_FIELD_BY_MODE = { web: "user_location_timezone_web", hybrid: "user_location_timezone_hybrid" };
const INCLUDE_SEARCH_SOURCES_FIELD_BY_MODE = { web: "include_search_sources_web", hybrid: "include_search_sources_hybrid" };
const SHOW_REASONING_FIELD_BY_MODE = { web: "show_reasoning_web", hybrid: "show_reasoning_hybrid" };
const VERBOSITY_FIELD_BY_MODE = { web: "text_verbosity_web", hybrid: "text_verbosity_hybrid" };
// Azure client timeout (seconds) and SDK auto-retry count — independently
// configurable per mode, same pattern as everything else in this card. No
// backend default: "180"/"0" below are this form's own defaults, saved to
// AIProfile like every other field here, not a hidden runtime fallback.
const AZURE_TIMEOUT_FIELD_BY_MODE = { web: "azure_timeout_seconds_web", hybrid: "azure_timeout_seconds_hybrid" };
const AZURE_MAX_RETRIES_FIELD_BY_MODE = { web: "azure_max_retries_web", hybrid: "azure_max_retries_hybrid" };

// user_location is stored server-side as one JSON string per mode — exploded
// into 4 separate form fields here for editable inputs, re-serialized back to
// JSON only when saving.
const parseUserLocation = (jsonStr) => {
  if (!jsonStr) return { country: "", city: "", region: "", timezone: "" };
  try {
    const parsed = JSON.parse(jsonStr);
    return {
      country: parsed.country || "",
      city: parsed.city || "",
      region: parsed.region || "",
      timezone: parsed.timezone || "",
    };
  } catch {
    return { country: "", city: "", region: "", timezone: "" };
  }
};

const serializeUserLocation = (country, city, region, timezone) => {
  const fields = {};
  if (country) fields.country = country;
  if (city) fields.city = city;
  if (region) fields.region = region;
  if (timezone) fields.timezone = timezone;
  return Object.keys(fields).length ? JSON.stringify(fields) : null;
};

const SystemPrompts = () => {
  const { t } = useTranslation();
  const { aispaceColor } = useThemeColor();
  const { addToast } = useToast();
  const dispatch = useDispatch();
  useSelector(selectAIProfile); // Profile state available in Redux
  const selectedTask = useSelector(selectSelectedTask);

  // Form state
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    description: "",
    task_id: selectedTask?.id,
    top_k_documents: 5,
    use_custom_top_k_documents: false,
    top_k_hybrid: 5,
    use_custom_top_k_hybrid: false,
    qa_pairs_documents: 5,
    qa_pairs_web: 5,
    qa_pairs_hybrid: 5,
    use_custom_qa_pairs_documents: false,
    search_type_documents: "hybrid",
    search_type_hybrid: "hybrid",
    vector_k_documents: 20,
    use_custom_vector_k_documents: false,
    vector_k_hybrid: 20,
    use_custom_vector_k_hybrid: false,
    model_documents: "gpt-5.1",
    model_web: "gpt-5.1",
    model_hybrid: "gpt-5.1",
    reasoning_effort_documents: "none",
    reasoning_effort_web: "none",
    reasoning_effort_hybrid: "none",
    max_completion_tokens_documents: 4096,
    web_search_enabled: false,
    web_search_custom_prompt: "",
    knowledge_source: "documents",
    system_prompt_documents: "",
    system_prompt_web: "",
    system_prompt_hybrid: "",
    search_context_size_web: "medium",
    search_context_size_hybrid: "medium",
    regex_detection_documents: true,
    regex_detection_web: true,
    regex_detection_hybrid: true,
    use_web_chat_history_web: false,
    use_web_chat_history_hybrid: false,
    force_web_search_web: true,
    force_web_search_hybrid: true,
    stream_response_web: true,
    stream_response_hybrid: true,
    max_tool_calls_web: "10",
    max_tool_calls_hybrid: "10",
    azure_timeout_seconds_web: "180",
    azure_timeout_seconds_hybrid: "180",
    azure_max_retries_web: "0",
    azure_max_retries_hybrid: "0",
    max_output_tokens_web: "1500",
    max_output_tokens_hybrid: "1500",
    allowed_domains_web: "",
    allowed_domains_hybrid: "",
    blocked_domains_web: "",
    blocked_domains_hybrid: "",
    user_location_enabled_web: false,
    user_location_enabled_hybrid: false,
    user_location_country_web: "",
    user_location_city_web: "",
    user_location_region_web: "",
    user_location_timezone_web: "",
    user_location_country_hybrid: "",
    user_location_city_hybrid: "",
    user_location_region_hybrid: "",
    user_location_timezone_hybrid: "",
    include_search_sources_web: false,
    include_search_sources_hybrid: false,
    show_reasoning_web: false,
    show_reasoning_hybrid: false,
    text_verbosity_web: "",
    text_verbosity_hybrid: "",
  });

  const [initialData, setInitialData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [localError, setLocalError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  const [isSearchStrategyDropdownOpen, setIsSearchStrategyDropdownOpen] = useState(false);
  const modelDropdownRef = useRef(null);
  const searchStrategyDropdownRef = useRef(null);

  const { data } = useSelector((state) => state.admin);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(event.target)) {
        setIsModelDropdownOpen(false);
      }
      if (searchStrategyDropdownRef.current && !searchStrategyDropdownRef.current.contains(event.target)) {
        setIsSearchStrategyDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const isAdmin = data?.user_role === "ADMIN";
  const isSuperAdmin = data?.user_role === "SUPER_ADMIN"; // only SUPER_ADMIN can edit AI Profile
  const userOrganizationId = data?.organization_id;

  // Load AI Profile when task changes
  useEffect(() => {
    const loadAIProfile = async () => {
      if (selectedTask?.id) {
        setIsFetching(true);
        dispatch(setLoading(true));
        try {
          const profile = await fetchAIProfileByTask(selectedTask.id);
          if (!profile) {
            dispatch(clearAIProfile());
            resetForm();
            return;
          }
          dispatch(setAIProfile(profile));
          const loadedData = {
            id: profile.id,
            name: profile.name || "",
            description: profile.description || "",
            task_id: profile.task_id,
            top_k_documents: profile.top_k_documents != null ? parseInt(profile.top_k_documents, 10) : 8,
            use_custom_top_k_documents: profile.use_custom_top_k_documents != null ? Boolean(profile.use_custom_top_k_documents) : false,
            top_k_hybrid: profile.top_k_hybrid != null ? parseInt(profile.top_k_hybrid, 10) : 8,
            use_custom_top_k_hybrid: profile.use_custom_top_k_hybrid != null ? Boolean(profile.use_custom_top_k_hybrid) : false,
            qa_pairs_documents: profile.qa_pairs_documents != null ? parseInt(profile.qa_pairs_documents, 10) : 3,
            qa_pairs_web: profile.qa_pairs_web != null ? parseInt(profile.qa_pairs_web, 10) : 3,
            qa_pairs_hybrid: profile.qa_pairs_hybrid != null ? parseInt(profile.qa_pairs_hybrid, 10) : 3,
            use_custom_qa_pairs_documents: profile.use_custom_qa_pairs_documents != null ? Boolean(profile.use_custom_qa_pairs_documents) : false,
            search_type_documents: profile.search_type_documents || "hybrid",
            search_type_hybrid: profile.search_type_hybrid || "hybrid",
            vector_k_documents: profile.vector_k_documents != null ? parseInt(profile.vector_k_documents, 10) : 20,
            use_custom_vector_k_documents: profile.use_custom_vector_k_documents != null ? Boolean(profile.use_custom_vector_k_documents) : false,
            vector_k_hybrid: profile.vector_k_hybrid != null ? parseInt(profile.vector_k_hybrid, 10) : 20,
            use_custom_vector_k_hybrid: profile.use_custom_vector_k_hybrid != null ? Boolean(profile.use_custom_vector_k_hybrid) : false,
            model_documents: profile.model_documents || "gpt-5.1",
            model_web: profile.model_web || "gpt-5.1",
            model_hybrid: profile.model_hybrid || "gpt-5.1",
            reasoning_effort_documents: profile.reasoning_effort_documents || "none",
            reasoning_effort_web: profile.reasoning_effort_web || "none",
            reasoning_effort_hybrid: profile.reasoning_effort_hybrid || "none",
            max_completion_tokens_documents: profile.max_completion_tokens_documents != null ? parseInt(profile.max_completion_tokens_documents, 10) : 4096,
            web_search_enabled: profile.web_search_enabled != null ? Boolean(profile.web_search_enabled) : false,
            web_search_custom_prompt: profile.web_search_custom_prompt || "",
            knowledge_source: profile.knowledge_source || "documents",
            system_prompt_documents: profile.system_prompt_documents || "",
            system_prompt_web: profile.system_prompt_web || "",
            system_prompt_hybrid: profile.system_prompt_hybrid || "",
            search_context_size_web: profile.search_context_size_web || "medium",
            search_context_size_hybrid: profile.search_context_size_hybrid || "medium",
            regex_detection_documents: profile.regex_detection_documents != null ? Boolean(profile.regex_detection_documents) : true,
            regex_detection_web: profile.regex_detection_web != null ? Boolean(profile.regex_detection_web) : true,
            regex_detection_hybrid: profile.regex_detection_hybrid != null ? Boolean(profile.regex_detection_hybrid) : true,
            use_web_chat_history_web: profile.use_web_chat_history_web != null ? Boolean(profile.use_web_chat_history_web) : false,
            use_web_chat_history_hybrid: profile.use_web_chat_history_hybrid != null ? Boolean(profile.use_web_chat_history_hybrid) : false,
            force_web_search_web: profile.force_web_search_web != null ? Boolean(profile.force_web_search_web) : true,
            force_web_search_hybrid: profile.force_web_search_hybrid != null ? Boolean(profile.force_web_search_hybrid) : true,
            stream_response_web: profile.stream_response_web != null ? Boolean(profile.stream_response_web) : true,
            stream_response_hybrid: profile.stream_response_hybrid != null ? Boolean(profile.stream_response_hybrid) : true,
            max_tool_calls_web: profile.max_tool_calls_web != null ? String(profile.max_tool_calls_web) : "10",
            max_tool_calls_hybrid: profile.max_tool_calls_hybrid != null ? String(profile.max_tool_calls_hybrid) : "10",
            azure_timeout_seconds_web: profile.azure_timeout_seconds_web != null ? String(profile.azure_timeout_seconds_web) : "180",
            azure_timeout_seconds_hybrid: profile.azure_timeout_seconds_hybrid != null ? String(profile.azure_timeout_seconds_hybrid) : "180",
            azure_max_retries_web: profile.azure_max_retries_web != null ? String(profile.azure_max_retries_web) : "0",
            azure_max_retries_hybrid: profile.azure_max_retries_hybrid != null ? String(profile.azure_max_retries_hybrid) : "0",
            max_output_tokens_web: profile.max_output_tokens_web != null ? String(profile.max_output_tokens_web) : "1500",
            max_output_tokens_hybrid: profile.max_output_tokens_hybrid != null ? String(profile.max_output_tokens_hybrid) : "1500",
            allowed_domains_web: profile.allowed_domains_web || "",
            allowed_domains_hybrid: profile.allowed_domains_hybrid || "",
            blocked_domains_web: profile.blocked_domains_web || "",
            blocked_domains_hybrid: profile.blocked_domains_hybrid || "",
            user_location_enabled_web: profile.user_location_enabled_web != null ? Boolean(profile.user_location_enabled_web) : false,
            user_location_enabled_hybrid: profile.user_location_enabled_hybrid != null ? Boolean(profile.user_location_enabled_hybrid) : false,
            ...(() => {
              const web = parseUserLocation(profile.user_location_web);
              const hybrid = parseUserLocation(profile.user_location_hybrid);
              return {
                user_location_country_web: web.country,
                user_location_city_web: web.city,
                user_location_region_web: web.region,
                user_location_timezone_web: web.timezone,
                user_location_country_hybrid: hybrid.country,
                user_location_city_hybrid: hybrid.city,
                user_location_region_hybrid: hybrid.region,
                user_location_timezone_hybrid: hybrid.timezone,
              };
            })(),
            include_search_sources_web: profile.include_search_sources_web != null ? Boolean(profile.include_search_sources_web) : false,
            include_search_sources_hybrid: profile.include_search_sources_hybrid != null ? Boolean(profile.include_search_sources_hybrid) : false,
            show_reasoning_web: profile.show_reasoning_web != null ? Boolean(profile.show_reasoning_web) : false,
            show_reasoning_hybrid: profile.show_reasoning_hybrid != null ? Boolean(profile.show_reasoning_hybrid) : false,
            text_verbosity_web: profile.text_verbosity_web || "",
            text_verbosity_hybrid: profile.text_verbosity_hybrid || "",
          };
          setFormData(loadedData);
          setInitialData(loadedData);
          setIsEditing(true);
          setHasChanges(false);
        } catch (err) {
          if (err.response?.status !== 404) {
            const errorMessage = err.response?.data?.detail || t('agentSettings.aiProfileLoadError');
            dispatch(setError(errorMessage));
            setLocalError(errorMessage);
          } else {
            dispatch(clearAIProfile());
            resetForm();
          }
        } finally {
          dispatch(setLoading(false));
          setIsFetching(false);
        }
      }
    };
    loadAIProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTask?.id, dispatch]);

  // Check for changes
  useEffect(() => {
    if (initialData) {
      const changed = JSON.stringify(formData) !== JSON.stringify(initialData);
      setHasChanges(changed);
    }
  }, [formData, initialData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Warns the admin the moment the typed value crosses the 65536 ceiling
  // (matching the Responses API's own max_output_tokens limit and the
  // save-time clamp below) instead of only finding out after Save silently
  // rewrote it. Only fires on the crossing itself, not on every keystroke
  // while already over the limit, so retyping/adding digits past it doesn't
  // spam the toast.
  const handleMaxOutputTokensChange = (field, rawValue) => {
    const prevParsed = parseInt(formData[field], 10);
    const nextParsed = parseInt(rawValue, 10);
    if (nextParsed > 65536 && !(prevParsed > 65536)) {
      addToast(t('agentSettings.aiProfileMaxOutputTokensExceeded', { max: 65536 }), 'error');
    }
    setFormData((prev) => ({ ...prev, [field]: rawValue }));
  };

  // Unlike handleMaxOutputTokensChange above (which warns but still lets the
  // over-limit value sit in the field until Save clamps it), Response Length
  // Limit rejects the keystroke outright — the field simply doesn't accept
  // anything past 65536, alerting each time an attempt is made instead of
  // silently truncating on save.
  const handleResponseLengthChange = (e) => {
    const rawValue = e.target.value;
    const parsed = parseInt(rawValue, 10);
    if (!isNaN(parsed) && parsed > 65536) {
      addToast(t('agentSettings.aiProfileResponseLengthExceeded', { max: 65536 }), 'error');
      return;
    }
    setFormData((prev) => ({ ...prev, max_completion_tokens_documents: rawValue }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    setLocalError(null);

    if (isAdmin && !isSuperAdmin) {
      if (selectedTask?.organization_id && selectedTask.organization_id !== userOrganizationId) {
        setLocalError(t('agentSettings.aiProfileNoPermission'));
        setIsLoading(false);
        return;
      }
    }

    try {
      const parsedTokens = parseInt(formData.max_completion_tokens_documents, 10);
      const parsedMaxToolCallsWeb = parseInt(formData.max_tool_calls_web, 10);
      const parsedMaxToolCallsHybrid = parseInt(formData.max_tool_calls_hybrid, 10);
      // Hard-clamped to 65536 here too (not just server-side, and matching
      // the Responses API's own max_output_tokens ceiling) so the admin sees
      // the real value that will be saved, not a silently-different one.
      // Independent per mode — Web and Hybrid each parsed/clamped on their
      // own, matching how they're stored (max_output_tokens_web/hybrid) and
      // edited (one field per mode, see MAX_OUTPUT_TOKENS_FIELD_BY_MODE).
      const clampMaxOutputTokens = (raw) => {
        const parsed = parseInt(raw, 10);
        return isNaN(parsed) || parsed < 1 ? 1500 : Math.min(parsed, 65536);
      };
      // Same "clamp here too, not just server-side" reasoning as
      // clampMaxOutputTokens above, and same per-mode independence (Web and
      // Hybrid each have their own azure_timeout_seconds_*/azure_max_retries_*).
      const clampAzureTimeout = (raw) => {
        const parsed = parseInt(raw, 10);
        return isNaN(parsed) || parsed < 10 ? 180 : Math.min(parsed, 600);
      };
      const clampAzureMaxRetries = (raw) => {
        const parsed = parseInt(raw, 10);
        return isNaN(parsed) || parsed < 0 ? 0 : Math.min(parsed, 3);
      };
      const submitData = {
        ...formData,
        task_id: selectedTask?.id,
        max_completion_tokens_documents: isNaN(parsedTokens) || parsedTokens < 1 ? 4096 : parsedTokens,
        max_tool_calls_web: isNaN(parsedMaxToolCallsWeb) ? null : parsedMaxToolCallsWeb,
        max_tool_calls_hybrid: isNaN(parsedMaxToolCallsHybrid) ? null : parsedMaxToolCallsHybrid,
        max_output_tokens_web: clampMaxOutputTokens(formData.max_output_tokens_web),
        max_output_tokens_hybrid: clampMaxOutputTokens(formData.max_output_tokens_hybrid),
        azure_timeout_seconds_web: clampAzureTimeout(formData.azure_timeout_seconds_web),
        azure_timeout_seconds_hybrid: clampAzureTimeout(formData.azure_timeout_seconds_hybrid),
        azure_max_retries_web: clampAzureMaxRetries(formData.azure_max_retries_web),
        azure_max_retries_hybrid: clampAzureMaxRetries(formData.azure_max_retries_hybrid),
        user_location_web: formData.user_location_enabled_web
          ? serializeUserLocation(formData.user_location_country_web, formData.user_location_city_web, formData.user_location_region_web, formData.user_location_timezone_web)
          : null,
        user_location_hybrid: formData.user_location_enabled_hybrid
          ? serializeUserLocation(formData.user_location_country_hybrid, formData.user_location_city_hybrid, formData.user_location_region_hybrid, formData.user_location_timezone_hybrid)
          : null,
      };

      if (isEditing && formData.id) {
        await updateAIProfile(submitData);
      } else {
        await createAIProfile(submitData);
      }

      const refreshed = await fetchAIProfileByTask(selectedTask.id);
      if (!refreshed) return;
      dispatch(setAIProfile(refreshed));

      const newData = {
        id: refreshed.id,
        name: refreshed.name || "",
        description: refreshed.description || "",
        task_id: refreshed.task_id,
        top_k_documents: refreshed.top_k_documents != null ? parseInt(refreshed.top_k_documents, 10) : formData.top_k_documents,
        use_custom_top_k_documents: refreshed.use_custom_top_k_documents != null ? Boolean(refreshed.use_custom_top_k_documents) : formData.use_custom_top_k_documents,
        top_k_hybrid: refreshed.top_k_hybrid != null ? parseInt(refreshed.top_k_hybrid, 10) : formData.top_k_hybrid,
        use_custom_top_k_hybrid: refreshed.use_custom_top_k_hybrid != null ? Boolean(refreshed.use_custom_top_k_hybrid) : formData.use_custom_top_k_hybrid,
        qa_pairs_documents: refreshed.qa_pairs_documents != null ? parseInt(refreshed.qa_pairs_documents, 10) : formData.qa_pairs_documents,
        qa_pairs_web: refreshed.qa_pairs_web != null ? parseInt(refreshed.qa_pairs_web, 10) : formData.qa_pairs_web,
        qa_pairs_hybrid: refreshed.qa_pairs_hybrid != null ? parseInt(refreshed.qa_pairs_hybrid, 10) : formData.qa_pairs_hybrid,
        use_custom_qa_pairs_documents: refreshed.use_custom_qa_pairs_documents != null ? Boolean(refreshed.use_custom_qa_pairs_documents) : formData.use_custom_qa_pairs_documents,
        search_type_documents: refreshed.search_type_documents || formData.search_type_documents,
        search_type_hybrid: refreshed.search_type_hybrid || formData.search_type_hybrid,
        vector_k_documents: refreshed.vector_k_documents != null ? parseInt(refreshed.vector_k_documents, 10) : formData.vector_k_documents,
        use_custom_vector_k_documents: refreshed.use_custom_vector_k_documents != null ? Boolean(refreshed.use_custom_vector_k_documents) : formData.use_custom_vector_k_documents,
        vector_k_hybrid: refreshed.vector_k_hybrid != null ? parseInt(refreshed.vector_k_hybrid, 10) : formData.vector_k_hybrid,
        use_custom_vector_k_hybrid: refreshed.use_custom_vector_k_hybrid != null ? Boolean(refreshed.use_custom_vector_k_hybrid) : formData.use_custom_vector_k_hybrid,
        model_documents: refreshed.model_documents || formData.model_documents,
        model_web: refreshed.model_web || formData.model_web,
        model_hybrid: refreshed.model_hybrid || formData.model_hybrid,
        reasoning_effort_documents: refreshed.reasoning_effort_documents || formData.reasoning_effort_documents,
        reasoning_effort_web: refreshed.reasoning_effort_web || formData.reasoning_effort_web,
        reasoning_effort_hybrid: refreshed.reasoning_effort_hybrid || formData.reasoning_effort_hybrid,
        max_completion_tokens_documents: (() => { const v = parseInt(refreshed.max_completion_tokens_documents, 10); return isNaN(v) ? 4096 : v; })(),
        web_search_enabled: refreshed.web_search_enabled != null ? Boolean(refreshed.web_search_enabled) : false,
        web_search_custom_prompt: refreshed.web_search_custom_prompt || "",
        knowledge_source: refreshed.knowledge_source || "documents",
        system_prompt_documents: refreshed.system_prompt_documents || "",
        system_prompt_web: refreshed.system_prompt_web || "",
        system_prompt_hybrid: refreshed.system_prompt_hybrid || "",
        search_context_size_web: refreshed.search_context_size_web || formData.search_context_size_web,
        search_context_size_hybrid: refreshed.search_context_size_hybrid || formData.search_context_size_hybrid,
        regex_detection_documents: refreshed.regex_detection_documents != null ? Boolean(refreshed.regex_detection_documents) : formData.regex_detection_documents,
        regex_detection_web: refreshed.regex_detection_web != null ? Boolean(refreshed.regex_detection_web) : formData.regex_detection_web,
        regex_detection_hybrid: refreshed.regex_detection_hybrid != null ? Boolean(refreshed.regex_detection_hybrid) : formData.regex_detection_hybrid,
        use_web_chat_history_web: refreshed.use_web_chat_history_web != null ? Boolean(refreshed.use_web_chat_history_web) : formData.use_web_chat_history_web,
        use_web_chat_history_hybrid: refreshed.use_web_chat_history_hybrid != null ? Boolean(refreshed.use_web_chat_history_hybrid) : formData.use_web_chat_history_hybrid,
        force_web_search_web: refreshed.force_web_search_web != null ? Boolean(refreshed.force_web_search_web) : formData.force_web_search_web,
        force_web_search_hybrid: refreshed.force_web_search_hybrid != null ? Boolean(refreshed.force_web_search_hybrid) : formData.force_web_search_hybrid,
        stream_response_web: refreshed.stream_response_web != null ? Boolean(refreshed.stream_response_web) : formData.stream_response_web,
        stream_response_hybrid: refreshed.stream_response_hybrid != null ? Boolean(refreshed.stream_response_hybrid) : formData.stream_response_hybrid,
        max_tool_calls_web: refreshed.max_tool_calls_web != null ? String(refreshed.max_tool_calls_web) : "10",
        max_tool_calls_hybrid: refreshed.max_tool_calls_hybrid != null ? String(refreshed.max_tool_calls_hybrid) : "10",
        azure_timeout_seconds_web: refreshed.azure_timeout_seconds_web != null ? String(refreshed.azure_timeout_seconds_web) : "180",
        azure_timeout_seconds_hybrid: refreshed.azure_timeout_seconds_hybrid != null ? String(refreshed.azure_timeout_seconds_hybrid) : "180",
        azure_max_retries_web: refreshed.azure_max_retries_web != null ? String(refreshed.azure_max_retries_web) : "0",
        azure_max_retries_hybrid: refreshed.azure_max_retries_hybrid != null ? String(refreshed.azure_max_retries_hybrid) : "0",
        max_output_tokens_web: refreshed.max_output_tokens_web != null ? String(refreshed.max_output_tokens_web) : "1500",
        max_output_tokens_hybrid: refreshed.max_output_tokens_hybrid != null ? String(refreshed.max_output_tokens_hybrid) : "1500",
        allowed_domains_web: refreshed.allowed_domains_web || "",
        allowed_domains_hybrid: refreshed.allowed_domains_hybrid || "",
        blocked_domains_web: refreshed.blocked_domains_web || "",
        blocked_domains_hybrid: refreshed.blocked_domains_hybrid || "",
        user_location_enabled_web: refreshed.user_location_enabled_web != null ? Boolean(refreshed.user_location_enabled_web) : false,
        user_location_enabled_hybrid: refreshed.user_location_enabled_hybrid != null ? Boolean(refreshed.user_location_enabled_hybrid) : false,
        ...(() => {
          const web = parseUserLocation(refreshed.user_location_web);
          const hybrid = parseUserLocation(refreshed.user_location_hybrid);
          return {
            user_location_country_web: web.country,
            user_location_city_web: web.city,
            user_location_region_web: web.region,
            user_location_timezone_web: web.timezone,
            user_location_country_hybrid: hybrid.country,
            user_location_city_hybrid: hybrid.city,
            user_location_region_hybrid: hybrid.region,
            user_location_timezone_hybrid: hybrid.timezone,
          };
        })(),
        include_search_sources_web: refreshed.include_search_sources_web != null ? Boolean(refreshed.include_search_sources_web) : false,
        include_search_sources_hybrid: refreshed.include_search_sources_hybrid != null ? Boolean(refreshed.include_search_sources_hybrid) : false,
        show_reasoning_web: refreshed.show_reasoning_web != null ? Boolean(refreshed.show_reasoning_web) : false,
        show_reasoning_hybrid: refreshed.show_reasoning_hybrid != null ? Boolean(refreshed.show_reasoning_hybrid) : false,
        text_verbosity_web: refreshed.text_verbosity_web || "",
        text_verbosity_hybrid: refreshed.text_verbosity_hybrid || "",
      };

      setFormData(newData);
      setInitialData(newData);
      setIsEditing(true);
      setHasChanges(false);
      addToast(t('agentSettings.aiProfileSaveSuccess'), 'success');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || t('agentSettings.aiProfileSaveError');
      setLocalError(errorMessage);
      dispatch(setError(errorMessage));
      addToast(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (initialData) {
      setFormData(initialData);
      setHasChanges(false);
    }
  };

  const resetForm = () => {
    const defaultData = {
      id: null,
      name: "",
      description: "",
      task_id: selectedTask?.id,
      top_k_documents: 5,
      use_custom_top_k_documents: false,
      top_k_hybrid: 5,
      use_custom_top_k_hybrid: false,
      qa_pairs_documents: 5,
      qa_pairs_web: 5,
      qa_pairs_hybrid: 5,
      use_custom_qa_pairs_documents: false,
      search_type_documents: "hybrid",
      search_type_hybrid: "hybrid",
      vector_k_documents: 20,
      use_custom_vector_k_documents: false,
      vector_k_hybrid: 20,
      use_custom_vector_k_hybrid: false,
      model_documents: "gpt-5.1",
      model_web: "gpt-5.1",
      model_hybrid: "gpt-5.1",
      reasoning_effort_documents: "none",
      reasoning_effort_web: "none",
      reasoning_effort_hybrid: "none",
      max_completion_tokens_documents: 4096,
      web_search_enabled: false,
      web_search_custom_prompt: "",
      knowledge_source: "documents",
      system_prompt_documents: "",
      system_prompt_web: "",
      system_prompt_hybrid: "",
      search_context_size_web: "medium",
      search_context_size_hybrid: "medium",
      regex_detection_documents: true,
      regex_detection_web: true,
      regex_detection_hybrid: true,
      use_web_chat_history_web: false,
      use_web_chat_history_hybrid: false,
      force_web_search_web: true,
      force_web_search_hybrid: true,
      stream_response_web: true,
      stream_response_hybrid: true,
      max_tool_calls_web: "10",
      max_tool_calls_hybrid: "10",
      azure_timeout_seconds_web: "180",
      azure_timeout_seconds_hybrid: "180",
      azure_max_retries_web: "0",
      azure_max_retries_hybrid: "0",
      max_output_tokens_web: "1500",
      max_output_tokens_hybrid: "1500",
      allowed_domains_web: "",
      allowed_domains_hybrid: "",
      blocked_domains_web: "",
      blocked_domains_hybrid: "",
      user_location_enabled_web: false,
      user_location_enabled_hybrid: false,
      user_location_country_web: "",
      user_location_city_web: "",
      user_location_region_web: "",
      user_location_timezone_web: "",
      user_location_country_hybrid: "",
      user_location_city_hybrid: "",
      user_location_region_hybrid: "",
      user_location_timezone_hybrid: "",
      include_search_sources_web: false,
      include_search_sources_hybrid: false,
      show_reasoning_web: false,
      show_reasoning_hybrid: false,
      text_verbosity_web: "",
      text_verbosity_hybrid: "",
    };
    setFormData(defaultData);
    setInitialData(defaultData);
    setIsEditing(false);
    setHasChanges(false);
  };

  // Reasoning Effort / Verbosity / the disabled-Temperature-and-Top-P controls
  // below were all gated on the exact string "gpt-5.1" — before the 5.6
  // variants existed, that was equivalent to "any GPT-5 model". It no
  // longer is: the backend's own gating (question.py's `is_gpt5`) already
  // matches any model name containing "gpt-5", so it silently supports these
  // controls for the two new models — the UI just never let anyone see or
  // set them. Matching the backend's own check here instead of one specific
  // model string means any future gpt-5.x model gets this automatically too.
  const isGpt5Family = (formData[MODEL_FIELD_BY_MODE[formData.knowledge_source] || 'model_documents'] || '').toLowerCase().includes('gpt-5');
  // The 5.6 variants were initially offered only for Web/Hybrid (admin
  // evaluating web-search answer quality). Documents mode resolves them through
  // the same MODEL_DEPLOYMENT_MAPPING as gpt-5.1, and its is_gpt5 check matches
  // any "gpt-5" model name — so no backend change is needed to offer them here.
  // gpt-4o retired: no longer offered as a choice for new/updated profiles.
  // Backend mapping (config.py MODEL_DEPLOYMENT_MAPPING) is left in place so
  // any AIProfile row still holding a previously-saved model='gpt-4o' keeps
  // resolving correctly instead of breaking.
  const modelOptions = [
    { value: "default", label: t('agentSettings.aiProfileModelDefaultLabel'), description: t('agentSettings.aiProfileModelDefaultDesc') },
    { value: "gpt-5.1", label: t('agentSettings.aiProfileModelGpt51Label'), description: t('agentSettings.aiProfileModelGpt51Desc') },
    { value: "gpt-5.6-luna", label: t('agentSettings.aiProfileModelGpt56LunaLabel'), description: t('agentSettings.aiProfileModelGpt56LunaDesc') },
    { value: "gpt-5.6-terra", label: t('agentSettings.aiProfileModelGpt56TerraLabel'), description: t('agentSettings.aiProfileModelGpt56TerraDesc') },
  ];

  const reasoningOptions = [
    { value: "none", label: t('agentSettings.aiProfileReasoningStandardLabel'), description: t('agentSettings.aiProfileReasoningStandardDesc') },
    { value: "low", label: t('agentSettings.aiProfileReasoningLowLabel'), description: t('agentSettings.aiProfileReasoningLowDesc') },
    { value: "medium", label: t('agentSettings.aiProfileReasoningMediumLabel'), description: t('agentSettings.aiProfileReasoningMediumDesc') },
    { value: "high", label: t('agentSettings.aiProfileReasoningHighLabel'), description: t('agentSettings.aiProfileReasoningHighDesc') },
  ];

  const verbosityOptions = [
    { value: "", label: t('agentSettings.aiProfileVerbosityOffLabel'), description: t('agentSettings.aiProfileVerbosityOffDesc') },
    { value: "low", label: t('agentSettings.aiProfileVerbosityLowLabel'), description: t('agentSettings.aiProfileVerbosityLowDesc') },
    { value: "medium", label: t('agentSettings.aiProfileVerbosityMediumLabel'), description: t('agentSettings.aiProfileVerbosityMediumDesc') },
    { value: "high", label: t('agentSettings.aiProfileVerbosityHighLabel'), description: t('agentSettings.aiProfileVerbosityHighDesc') },
  ];

  const searchContextSizeOptions = [
    { value: "low", label: t('agentSettings.aiProfileSearchContextLowLabel'), description: t('agentSettings.aiProfileSearchContextLowDesc') },
    { value: "medium", label: t('agentSettings.aiProfileSearchContextMediumLabel'), description: t('agentSettings.aiProfileSearchContextMediumDesc') },
    { value: "high", label: t('agentSettings.aiProfileSearchContextHighLabel'), description: t('agentSettings.aiProfileSearchContextHighDesc') },
  ];

  const searchStrategyOptions = [
    { value: "hybrid", label: t('agentSettings.aiProfileSearchHybrid') },
    { value: "semantic", label: t('agentSettings.aiProfileSearchSemantic') },
    { value: "vector", label: t('agentSettings.aiProfileSearchVector') },
    { value: "simple", label: t('agentSettings.aiProfileSearchKeyword') },
  ];

  const knowledgeSourceOptions = [
    {
      value: "documents",
      label: t('agentSettings.aiProfileModeDocumentsLabel'),
      description: t('agentSettings.aiProfileModeDocumentsDesc'),
      ragBadge: true,
      webBadge: false,
    },
    {
      value: "web",
      label: t('agentSettings.aiProfileModeWebLabel'),
      description: t('agentSettings.aiProfileModeWebDesc'),
      ragBadge: false,
      webBadge: true,
    },
    {
      value: "hybrid",
      label: t('agentSettings.aiProfileModeHybridLabel'),
      description: t('agentSettings.aiProfileModeHybridDesc'),
      ragBadge: true,
      webBadge: true,
    },
  ];

  const activeModeLabel = knowledgeSourceOptions.find(o => o.value === formData.knowledge_source)?.label
    || knowledgeSourceOptions[0].label;
  const activePromptPlaceholder = t('agentSettings.aiProfileSystemPromptPlaceholder', { mode: activeModeLabel });
  const activePromptField = PROMPT_FIELD_BY_MODE[formData.knowledge_source] || "system_prompt_documents";
  const activeMaxOutputTokensField = MAX_OUTPUT_TOKENS_FIELD_BY_MODE[formData.knowledge_source] || "max_output_tokens_web";
  const activeRegexField = REGEX_DETECTION_FIELD_BY_MODE[formData.knowledge_source] || "regex_detection_documents";
  const activeVectorKField = VECTOR_K_FIELD_BY_MODE[formData.knowledge_source] || "vector_k_documents";
  const activeUseCustomVectorKField = USE_CUSTOM_VECTOR_K_FIELD_BY_MODE[formData.knowledge_source] || "use_custom_vector_k_documents";
  const activeModelField = MODEL_FIELD_BY_MODE[formData.knowledge_source] || "model_documents";
  const activeReasoningEffortField = REASONING_EFFORT_FIELD_BY_MODE[formData.knowledge_source] || "reasoning_effort_documents";
  const activeQaPairsField = QA_PAIRS_FIELD_BY_MODE[formData.knowledge_source] || "qa_pairs_documents";
  const activeTopKField = TOP_K_FIELD_BY_MODE[formData.knowledge_source] || "top_k_documents";
  const activeUseCustomTopKField = USE_CUSTOM_TOP_K_FIELD_BY_MODE[formData.knowledge_source] || "use_custom_top_k_documents";
  const activeSearchTypeField = SEARCH_TYPE_FIELD_BY_MODE[formData.knowledge_source] || "search_type_documents";
  const activeSearchContextSizeField = SEARCH_CONTEXT_SIZE_FIELD_BY_MODE[formData.knowledge_source] || "search_context_size_web";
  const activeUseWebChatHistoryField = USE_WEB_CHAT_HISTORY_FIELD_BY_MODE[formData.knowledge_source] || "use_web_chat_history_web";
  const activeForceWebSearchField = FORCE_WEB_SEARCH_FIELD_BY_MODE[formData.knowledge_source] || "force_web_search_web";
  const activeStreamResponseField = STREAM_RESPONSE_FIELD_BY_MODE[formData.knowledge_source] || "stream_response_web";
  const activeMaxToolCallsField = MAX_TOOL_CALLS_FIELD_BY_MODE[formData.knowledge_source] || "max_tool_calls_web";
  const activeAllowedDomainsField = ALLOWED_DOMAINS_FIELD_BY_MODE[formData.knowledge_source] || "allowed_domains_web";
  const activeBlockedDomainsField = BLOCKED_DOMAINS_FIELD_BY_MODE[formData.knowledge_source] || "blocked_domains_web";
  const activeUserLocationEnabledField = USER_LOCATION_ENABLED_FIELD_BY_MODE[formData.knowledge_source] || "user_location_enabled_web";
  const activeUserLocationCountryField = USER_LOCATION_COUNTRY_FIELD_BY_MODE[formData.knowledge_source] || "user_location_country_web";
  const activeUserLocationCityField = USER_LOCATION_CITY_FIELD_BY_MODE[formData.knowledge_source] || "user_location_city_web";
  const activeUserLocationRegionField = USER_LOCATION_REGION_FIELD_BY_MODE[formData.knowledge_source] || "user_location_region_web";
  const activeUserLocationTimezoneField = USER_LOCATION_TIMEZONE_FIELD_BY_MODE[formData.knowledge_source] || "user_location_timezone_web";
  const activeIncludeSearchSourcesField = INCLUDE_SEARCH_SOURCES_FIELD_BY_MODE[formData.knowledge_source] || "include_search_sources_web";
  const activeShowReasoningField = SHOW_REASONING_FIELD_BY_MODE[formData.knowledge_source] || "show_reasoning_web";
  const activeVerbosityField = VERBOSITY_FIELD_BY_MODE[formData.knowledge_source] || "text_verbosity_web";
  const activeAzureTimeoutField = AZURE_TIMEOUT_FIELD_BY_MODE[formData.knowledge_source] || "azure_timeout_seconds_web";
  const activeAzureMaxRetriesField = AZURE_MAX_RETRIES_FIELD_BY_MODE[formData.knowledge_source] || "azure_max_retries_web";

  if (isFetching) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-10 h-10 rounded-full animate-spin mb-4" style={{ border: `3px solid ${aispaceColor}`, borderTopColor: 'transparent' }}></div>
        <p className="text-[14px] text-[#6B7280]">{t('agentSettings.aiProfileLoading')}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-[20px] font-semibold text-[#111827]">{t('agentSettings.aiProfileHeaderTitle')}</h2>
          <p className="text-[14px] text-[#6B7280] mt-1">
            {t('agentSettings.aiProfileHeaderSubtitle')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {(isSuperAdmin || isAdmin) && (
            <>
              <span className={`text-[14px] text-[#6B7280] ${hasChanges ? '' : 'invisible'}`}>{t('agentSettings.unsavedChanges')}</span>
              <button
                onClick={handleCancel}
                disabled={!hasChanges || isLoading}
                className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50"
              >
                {t('agentSettings.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={!hasChanges || isLoading}
                className="px-5 py-2.5 text-[14px] font-medium text-white rounded-lg transition-colors hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                style={{ backgroundColor: aispaceColor }}
              >
                {isLoading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    {t('agentSettings.saving')}
                  </>
                ) : (
                  t('agentSettings.save')
                )}
              </button>
            </>
          )}
        </div>
      </div>

      <div className={`mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-[14px] text-red-600 ${localError ? '' : 'hidden'}`}>
        {localError}
      </div>

      {/* Knowledge Source Card */}
      <div className="bg-white rounded-xl border border-[#EEEEEE] mb-6">
        <div className="flex items-start gap-4 p-6">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${aispaceColor}20` }}
          >
            <FileText size={20} style={{ color: aispaceColor }} />
          </div>
          <div>
            <h3 className="text-[16px] font-semibold text-[#111827]">{t('agentSettings.aiProfileKnowledgeSourceTitle')}</h3>
            <p className="text-[14px] text-[#6B7280] mt-0.5">
              {t('agentSettings.aiProfileKnowledgeSourceSubtitle')}
            </p>
          </div>
        </div>
        <div className="border-b border-[#EEEEEE]"></div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {knowledgeSourceOptions.map((option) => {
              const isSelected = formData.knowledge_source === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  disabled={!isSuperAdmin}
                  onClick={() => setFormData(prev => ({ ...prev, knowledge_source: option.value }))}
                  className="text-left p-4 rounded-xl border-2 transition-colors disabled:cursor-not-allowed relative"
                  style={{
                    borderColor: isSelected ? aispaceColor : '#E5E7EB',
                    backgroundColor: isSelected ? `${aispaceColor}08` : 'white',
                  }}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[14px] font-semibold text-[#111827]">{option.label}</span>
                    <span
                      className="w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center"
                      style={{ borderColor: isSelected ? aispaceColor : '#D1D5DB' }}
                    >
                      {isSelected && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: aispaceColor }} />}
                    </span>
                  </div>
                  <p className="text-[13px] text-[#6B7280] mb-3">{option.description}</p>
                  <div className="flex items-center gap-1.5">
                    <span
                      className="text-[11px] font-medium px-2 py-0.5 rounded-full"
                      style={option.ragBadge ? { backgroundColor: '#ECFDF5', color: '#059669' } : { backgroundColor: '#F3F4F6', color: '#6B7280' }}
                    >
                      {option.ragBadge ? t('agentSettings.aiProfileBadgeRag') : t('agentSettings.aiProfileBadgeNoRag')}
                    </span>
                    <span
                      className="text-[11px] font-medium px-2 py-0.5 rounded-full"
                      style={option.webBadge ? { backgroundColor: '#ECFDF5', color: '#059669' } : { backgroundColor: '#F3F4F6', color: '#6B7280' }}
                    >
                      {option.webBadge ? t('agentSettings.aiProfileBadgeWeb') : t('agentSettings.aiProfileBadgeNoWeb')}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* AI Identity Card */}
      <div className="bg-white rounded-xl border border-[#EEEEEE] mb-6">
        <div className="flex items-start gap-4 p-6">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${aispaceColor}20` }}
          >
            <Brain size={20} style={{ color: aispaceColor }} />
          </div>
          <div>
            <h3 className="text-[16px] font-semibold text-[#111827]">{t('agentSettings.aiProfileIdentityTitle')}</h3>
            <p className="text-[14px] text-[#6B7280] mt-0.5">
              {t('agentSettings.aiProfileIdentitySubtitle')}
            </p>
          </div>
        </div>
        <div className="border-b border-[#EEEEEE]"></div>
        <div className="py-6 pl-[100px] pr-[100px]">
          <div className="space-y-5">
            {/* Different system prompt per Knowledge Source — info callout */}
            <div
              className="rounded-xl px-4 py-3.5 flex items-start gap-3"
              style={{ backgroundColor: `${aispaceColor}0D`, border: `1px solid ${aispaceColor}33` }}
            >
              <CornerDownLeft size={18} className="mt-0.5 flex-shrink-0" style={{ color: aispaceColor }} />
              <div>
                <p className="text-[14px] font-semibold" style={{ color: aispaceColor }}>
                  {t('agentSettings.aiProfileDifferentPromptInfoTitle')}
                </p>
                <p className="text-[13px] text-[#6B7280] mt-0.5">
                  {t('agentSettings.aiProfileDifferentPromptInfoDesc')}
                </p>
              </div>
            </div>

            {/* AI Profile Name */}
            <div>
              <label className="block text-[14px] font-medium text-[#374151] mb-2">
                {t('agentSettings.aiProfileNameLabel')}
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={!isSuperAdmin}
                placeholder={t('agentSettings.aiProfileNamePlaceholder')}
                className="w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[var(--focus-color)] transition-colors disabled:bg-[#F9FAFB] disabled:cursor-not-allowed"
                style={{ '--focus-color': aispaceColor }}
              />
            </div>

            {/* Active prompt badge */}
            <div>
              <span
                className="inline-block text-[12px] font-medium px-2.5 py-1 rounded-full"
                style={{ backgroundColor: `${aispaceColor}15`, color: aispaceColor }}
              >
                {t('agentSettings.aiProfileActivePromptBadge', { mode: activeModeLabel })}
              </span>
            </div>

            {/* System Prompt — each Knowledge Source mode has its own dedicated
                field (system_prompt_documents/web/hybrid); switching modes swaps which
                field this textarea is bound to, so they never overwrite each other.
                Which mode is active is already shown by the badge above, so the
                label itself stays plain "System Prompt" without repeating the mode. */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[14px] font-medium text-[#374151]">
                  {t('agentSettings.aiProfileSystemPromptLabelPlain')}
                </label>
                {/* Regex Detection toggle — when off, the raw LLM answer is shown
                    as-is (no citation-pattern matching, no [N] marker synthesis,
                    no markdown-link stripping), so admins can see exactly what the
                    model produced while debugging/tuning a prompt. */}
                <div className="flex items-center gap-2">
                  <span className="text-[13px] text-[#6B7280]">{t('agentSettings.aiProfileRegexDetectionLabel')}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={formData[activeRegexField]}
                    disabled={!isSuperAdmin}
                    onClick={() => isSuperAdmin && setFormData(prev => ({ ...prev, [activeRegexField]: !prev[activeRegexField] }))}
                    className="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: formData[activeRegexField] ? aispaceColor : '#E5E7EB' }}
                  >
                    <span
                      className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
                      style={{ transform: formData[activeRegexField] ? 'translateX(22px)' : 'translateX(2px)' }}
                    />
                  </button>
                </div>
              </div>
              <textarea
                value={formData[activePromptField]}
                onChange={(e) => setFormData(prev => ({ ...prev, [activePromptField]: e.target.value }))}
                disabled={!isSuperAdmin}
                rows={8}
                placeholder={activePromptPlaceholder}
                className="w-full px-4 py-3 text-[13px] font-mono border border-[#E5E7EB] rounded-lg resize-none focus:outline-none focus:border-[var(--focus-color)] transition-colors disabled:bg-[#F9FAFB] disabled:cursor-not-allowed"
                style={{ '--focus-color': aispaceColor }}
              />
              <p className="text-[13px] text-[#9CA3AF] mt-2">
                {t('agentSettings.aiProfileSystemPromptHelp')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Model & Intelligence Settings Card */}
      <div className="bg-white rounded-xl border border-[#EEEEEE] mb-6">
        <div className="flex items-start gap-4 p-6">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${aispaceColor}20` }}
          >
            <BrainCog size={20} style={{ color: aispaceColor }} />
          </div>
          <div>
            <h3 className="text-[16px] font-semibold text-[#111827]">{t('agentSettings.aiProfileModelTitle')}</h3>
            <p className="text-[14px] text-[#6B7280] mt-0.5">
              {t('agentSettings.aiProfileModelSubtitle')}
            </p>
          </div>
        </div>
        <div className="border-b border-[#EEEEEE]"></div>
        <div className="py-6 pl-[100px] pr-[100px]">
          <div className="space-y-5">
            {/* Model Selection */}
            <div>
              <label className="block text-[14px] font-medium text-[#374151] mb-2">
                {t('agentSettings.aiProfileModelLabel')}
              </label>
              <div className="relative" ref={modelDropdownRef}>
                <button
                  type="button"
                  onClick={() => isSuperAdmin && setIsModelDropdownOpen(!isModelDropdownOpen)}
                  disabled={!isSuperAdmin}
                  className="w-full px-4 py-3 pr-10 text-[14px] text-left border border-[#E5E7EB] rounded-lg bg-white focus:outline-none focus:border-[var(--focus-color)] disabled:bg-[#F9FAFB] disabled:cursor-not-allowed"
                  style={{ '--focus-color': aispaceColor }}
                >
                  {modelOptions.find(o => o.value === formData[activeModelField])?.label || t('agentSettings.aiProfileModelSelectPlaceholder')}
                </button>
                <ChevronDown size={18} className={`absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-[#6B7280] transition-transform ${isModelDropdownOpen ? 'rotate-180' : ''}`} />

                {/* Custom Dropdown Menu */}
                {isModelDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-20 overflow-hidden">
                    {modelOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, [activeModelField]: option.value }));
                          setIsModelDropdownOpen(false);
                        }}
                        className={`w-full px-4 py-3 text-left text-[14px] hover:bg-[#F9FAFB] transition-colors ${
                          formData[activeModelField] === option.value ? 'bg-[#F3F4F6] font-medium' : 'text-[#374151]'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-[13px] text-[#9CA3AF] mt-2">
                {modelOptions.find(o => o.value === formData[activeModelField])?.description}
              </p>
            </div>

            {/* Reasoning Effort — toggle buttons, only when model is gpt-5.1 */}
            <div className={isGpt5Family ? '' : 'hidden'}>
              <label className="flex items-center gap-2 text-[14px] font-medium text-[#374151] mb-3">
                {t('agentSettings.aiProfileReasoningLabel')}
                <Info size={16} className="text-[#9CA3AF]" />
              </label>
              <div className="grid grid-cols-4 gap-3">
                {reasoningOptions.map((option) => {
                  const isSelected = (formData[activeReasoningEffortField] || '') === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, [activeReasoningEffortField]: option.value }))}
                      disabled={!isSuperAdmin}
                      className={`p-4 rounded-lg border-2 text-center transition-colors ${
                        isSelected
                          ? 'border-transparent text-white'
                          : 'border-[#E5E7EB] text-[#374151] hover:border-[#D1D5DB]'
                      } disabled:cursor-not-allowed`}
                      style={isSelected ? { backgroundColor: aispaceColor } : {}}
                    >
                      <span className="block text-[14px] font-medium">{option.label}</span>
                      <span className={`block text-[12px] mt-1 ${isSelected ? 'text-white/80' : 'text-[#6B7280]'}`}>
                        {option.description}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Response Length Limit — Web and Hybrid modes each have their own
                dedicated "Max Output Tokens: {mode}" cap below and no longer use
                this shared field (question.py's Web and Hybrid call sites now
                always pass max_completion_tokens=None); only Documents mode
                still uses it. */}
            {formData.knowledge_source !== 'web' && formData.knowledge_source !== 'hybrid' && (
              <div>
                <label className="block text-[14px] font-medium text-[#374151] mb-2">
                  {t('agentSettings.aiProfileResponseLengthLabel')}
                </label>
                <input
                  type="number"
                  name="max_completion_tokens"
                  value={formData.max_completion_tokens_documents || ""}
                  onChange={handleResponseLengthChange}
                  disabled={!isSuperAdmin}
                  placeholder="4096"
                  min="1000"
                  max="65536"
                  className="w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[var(--focus-color)] transition-colors disabled:bg-[#F9FAFB] disabled:cursor-not-allowed"
                  style={{ '--focus-color': aispaceColor }}
                />
                <p className="text-[13px] text-[#9CA3AF] mt-2">
                  {t('agentSettings.aiProfileResponseLengthHelp')}
                </p>
              </div>
            )}

            {/* Max Output Tokens safety cap — independently configurable per
                Knowledge Source mode (Web/Hybrid each have their own
                AIProfile column), same active-mode pattern as System Prompt
                above. Clamped to 65536 here and server-side, matching the
                Responses API's own max_output_tokens ceiling.
                Not shown for Documents mode: that mode has no field of its
                own here (max_output_tokens_documents was removed — it was
                never applied in question.py's generate_chat_response,
                Response Length Limit alone governs Documents mode's
                response length). */}
            {formData.knowledge_source !== 'documents' && (
            <div>
              <label className="block text-[14px] font-medium text-[#374151] mb-2">
                {t('agentSettings.aiProfileMaxOutputTokensLabel', { mode: activeModeLabel })}
              </label>
              <input
                type="number"
                value={formData[activeMaxOutputTokensField]}
                onChange={(e) => handleMaxOutputTokensChange(activeMaxOutputTokensField, e.target.value)}
                disabled={!isSuperAdmin}
                placeholder="1500"
                min="100"
                max="65536"
                className="w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[var(--focus-color)] transition-colors disabled:bg-[#F9FAFB] disabled:cursor-not-allowed"
                style={{ '--focus-color': aispaceColor }}
              />
              <p className="text-[13px] text-[#9CA3AF] mt-2">
                {t('agentSettings.aiProfileMaxOutputTokensHelp')}
              </p>
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Source Settings Card */}
      <div className="bg-white rounded-xl border border-[#EEEEEE]">
        <div className="flex items-start gap-4 p-6">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${aispaceColor}20` }}
          >
            <Database size={20} style={{ color: aispaceColor }} />
          </div>
          <div>
            <h3 className="text-[16px] font-semibold text-[#111827]">{t('agentSettings.aiProfileSourceSettingsTitle')}</h3>
            <p className="text-[14px] text-[#6B7280] mt-0.5">
              {formData.knowledge_source === 'web'
                ? t('agentSettings.aiProfileSourceSettingsSubtitleWeb')
                : formData.knowledge_source === 'hybrid'
                  ? t('agentSettings.aiProfileSourceSettingsSubtitleHybrid')
                  : t('agentSettings.aiProfileSourceSettingsSubtitleDocuments')}
            </p>
          </div>
        </div>
        <div className="border-b border-[#EEEEEE]"></div>
        <div className="py-6 pl-[100px] pr-[100px] space-y-5">
          {(formData.knowledge_source === 'documents' || formData.knowledge_source === 'hybrid') && (
            <div className="border border-[#EEEEEE] rounded-xl p-5">
              <div className="flex items-start gap-3 mb-5">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${aispaceColor}20` }}
                >
                  <Database size={16} style={{ color: aispaceColor }} />
                </div>
                <div>
                  <h4 className="text-[14px] font-semibold text-[#111827]">{t('agentSettings.aiProfileContextTitle')}</h4>
                  <p className="text-[13px] text-[#6B7280] mt-0.5">
                    {t('agentSettings.aiProfileContextSubtitle')}
                  </p>
                </div>
              </div>

              <div className="space-y-5">
                {/* Search Strategy */}
                <div>
                  <label className="block text-[14px] font-medium text-[#374151] mb-2">
                    {t('agentSettings.aiProfileSearchStrategyLabel')}
                  </label>
                  <div className="relative" ref={searchStrategyDropdownRef}>
                    <button
                      type="button"
                      onClick={() => isSuperAdmin && setIsSearchStrategyDropdownOpen(!isSearchStrategyDropdownOpen)}
                      disabled={!isSuperAdmin}
                      className="w-full px-4 py-3 pr-10 text-[14px] text-left border border-[#E5E7EB] rounded-lg bg-white focus:outline-none focus:border-[var(--focus-color)] disabled:bg-[#F9FAFB] disabled:cursor-not-allowed"
                      style={{ '--focus-color': aispaceColor }}
                    >
                      {searchStrategyOptions.find(o => o.value === formData[activeSearchTypeField])?.label || t('agentSettings.aiProfileSearchStrategyPlaceholder')}
                    </button>
                    <ChevronDown size={18} className={`absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-[#6B7280] transition-transform ${isSearchStrategyDropdownOpen ? 'rotate-180' : ''}`} />

                    {/* Custom Dropdown Menu */}
                    {isSearchStrategyDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-20 overflow-hidden">
                        {searchStrategyOptions.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, [activeSearchTypeField]: option.value }));
                              setIsSearchStrategyDropdownOpen(false);
                            }}
                            className={`w-full px-4 py-3 text-left text-[14px] hover:bg-[#F9FAFB] transition-colors ${
                              formData[activeSearchTypeField] === option.value ? 'bg-[#F3F4F6] font-medium' : 'text-[#374151]'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Document Context (Top K) */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-[14px] font-medium text-[#374151]">
                      {t('agentSettings.aiProfileTopKLabel')}
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-[13px] text-[#6B7280]">{t('agentSettings.aiProfileUseCustom')}</span>
                      <input
                        type="checkbox"
                        checked={formData[activeUseCustomTopKField]}
                        onChange={(e) => setFormData(prev => ({ ...prev, [activeUseCustomTopKField]: e.target.checked }))}
                        className="h-4 w-4 rounded border-[#D1D5DB]"
                        disabled={!isSuperAdmin}
                        style={{ accentColor: aispaceColor }}
                      />
                    </label>
                  </div>
                  <input
                    type="number"
                    name="top_k"
                    value={formData[activeTopKField]}
                    onChange={(e) => setFormData(prev => ({ ...prev, [activeTopKField]: Math.max(1, Math.min(50, Number(e.target.value))) }))}
                    disabled={!isSuperAdmin || !formData[activeUseCustomTopKField]}
                    min="1"
                    max="50"
                    className={`w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[var(--focus-color)] transition-colors disabled:bg-[#F9FAFB] disabled:cursor-not-allowed ${!formData[activeUseCustomTopKField] ? 'opacity-50' : ''}`}
                    style={{ '--focus-color': aispaceColor }}
                  />
                  {!formData[activeUseCustomTopKField] && (
                    <p className="text-[13px] text-[#9CA3AF] mt-2">
                      {t('agentSettings.aiProfileTopKDefaultHint')}
                    </p>
                  )}
                </div>

                {/* Vector Candidate Pool (Vector K) */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-[14px] font-medium text-[#374151]">
                      {t('agentSettings.aiProfileVectorKLabel')}
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-[13px] text-[#6B7280]">{t('agentSettings.aiProfileUseCustom')}</span>
                      <input
                        type="checkbox"
                        checked={formData[activeUseCustomVectorKField]}
                        onChange={(e) => setFormData(prev => ({ ...prev, [activeUseCustomVectorKField]: e.target.checked }))}
                        className="h-4 w-4 rounded border-[#D1D5DB]"
                        disabled={!isSuperAdmin}
                        style={{ accentColor: aispaceColor }}
                      />
                    </label>
                  </div>
                  <input
                    type="number"
                    name="vector_k"
                    value={formData[activeVectorKField]}
                    onChange={(e) => setFormData(prev => ({ ...prev, [activeVectorKField]: Math.max(1, Math.min(200, Number(e.target.value))) }))}
                    disabled={!isSuperAdmin || !formData[activeUseCustomVectorKField]}
                    min="1"
                    max="200"
                    className={`w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[var(--focus-color)] transition-colors disabled:bg-[#F9FAFB] disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${!formData[activeUseCustomVectorKField] ? 'opacity-50' : ''}`}
                    style={{ '--focus-color': aispaceColor }}
                  />
                  {!formData[activeUseCustomVectorKField] && (
                    <p className="text-[13px] text-[#9CA3AF] mt-2">
                      {t('agentSettings.aiProfileVectorKDefaultHint')}
                    </p>
                  )}
                </div>

                {/* Q/A Pairs as Context — Documents-only. Hybrid mode's qa_pairs_hybrid
                    is a different, unrelated control (it feeds the Web Source
                    Settings "Use Web Chat History" fetch count below), so this
                    documents-context section doesn't apply to Hybrid mode. */}
                {formData.knowledge_source === 'documents' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-[14px] font-medium text-[#374151]">
                      {t('agentSettings.aiProfileQaPairsLabel')}
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-[13px] text-[#6B7280]">{t('agentSettings.aiProfileUseCustom')}</span>
                      <input
                        type="checkbox"
                        checked={formData.use_custom_qa_pairs_documents}
                        onChange={(e) => setFormData(prev => ({ ...prev, use_custom_qa_pairs_documents: e.target.checked }))}
                        className="h-4 w-4 rounded border-[#D1D5DB]"
                        disabled={!isSuperAdmin}
                        style={{ accentColor: aispaceColor }}
                      />
                    </label>
                  </div>
                  <input
                    type="number"
                    name="qa_pairs"
                    value={formData[activeQaPairsField]}
                    onChange={(e) => setFormData(prev => ({ ...prev, [activeQaPairsField]: Math.max(1, Math.min(50, Number(e.target.value))) }))}
                    disabled={!isSuperAdmin || !formData.use_custom_qa_pairs_documents}
                    min="1"
                    max="50"
                    className={`w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[var(--focus-color)] transition-colors disabled:bg-[#F9FAFB] disabled:cursor-not-allowed ${!formData.use_custom_qa_pairs_documents ? 'opacity-50' : ''}`}
                    style={{ '--focus-color': aispaceColor }}
                  />
                  {!formData.use_custom_qa_pairs_documents && (
                    <p className="text-[13px] text-[#9CA3AF] mt-2">
                      {t('agentSettings.aiProfileQaPairsDefaultHint')}
                    </p>
                  )}
                </div>
                )}
              </div>
            </div>
          )}

          {(formData.knowledge_source === 'web' || formData.knowledge_source === 'hybrid') && (
            <div className="rounded-xl p-5" style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A' }}>
              <div className="flex items-start justify-between mb-5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                    <Globe size={16} style={{ color: '#D97706' }} />
                  </div>
                  <div>
                    <h4 className="text-[14px] font-semibold text-[#111827]">{t('agentSettings.aiProfileWebSourceSettingsTitle')}</h4>
                    <p className="text-[13px] text-[#6B7280] mt-0.5">
                      {t('agentSettings.aiProfileWebSourceSettingsSubtitle')}
                    </p>
                  </div>
                </div>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#FEF3C7', color: '#B45309' }}>
                  {t('agentSettings.aiProfileWebSourceBadge')}
                </span>
              </div>

              {/* Custom Q&A pairs for Web Search — off by default. Including
                  prior chat history previously degraded web search answer
                  quality; keep it opt-in per mode until an admin needs it. */}
              <div className="flex items-center justify-between mb-4 pb-4" style={{ borderBottom: '1px solid #FDE68A' }}>
                <div>
                  <label className="text-[14px] font-medium text-[#374151]">
                    {t('agentSettings.aiProfileUseWebChatHistoryLabel')}
                  </label>
                  <p className="text-[13px] text-[#92400E] mt-0.5">
                    {t('agentSettings.aiProfileUseWebChatHistoryHelp')}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData[activeUseWebChatHistoryField]}
                  disabled={!isSuperAdmin}
                  onClick={() => isSuperAdmin && setFormData(prev => ({ ...prev, [activeUseWebChatHistoryField]: !prev[activeUseWebChatHistoryField] }))}
                  className="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ml-4"
                  style={{ backgroundColor: formData[activeUseWebChatHistoryField] ? aispaceColor : '#E5E7EB' }}
                >
                  <span
                    className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
                    style={{ transform: formData[activeUseWebChatHistoryField] ? 'translateX(22px)' : 'translateX(2px)' }}
                  />
                </button>
              </div>

              <div>
                <label className="block text-[14px] font-medium text-[#374151] mb-2">
                  {t('agentSettings.aiProfileWebSearchProviderLabel')}
                </label>
                <div
                  className="w-full px-4 py-3 text-[14px] border rounded-lg bg-white text-[#111827]"
                  style={{ borderColor: '#FDE68A' }}
                >
                  {t('agentSettings.aiProfileWebSearchProviderValue')}
                </div>
              </div>

              {/* Search Context Size — the web_search tool's own knob for how much
                  web content Azure uses to ground the answer. Replaces the old
                  Max Web Results field, which only ever worked as an unreliable
                  "cite at most N sources" text hint appended to the prompt. */}
              <div className="mt-4">
                <label className="block text-[14px] font-medium text-[#374151] mb-3">
                  {t('agentSettings.aiProfileSearchContextSizeLabel')}
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {searchContextSizeOptions.map((option) => {
                    const isSelected = (formData[activeSearchContextSizeField] || 'medium') === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, [activeSearchContextSizeField]: option.value }))}
                        disabled={!isSuperAdmin}
                        className={`p-4 rounded-lg border-2 text-center transition-colors ${
                          isSelected
                            ? 'border-transparent text-white'
                            : 'border-[#FDE68A] text-[#374151] hover:border-[#D97706]'
                        } disabled:cursor-not-allowed bg-white`}
                        style={isSelected ? { backgroundColor: '#D97706' } : {}}
                      >
                        <span className="block text-[14px] font-medium">{option.label}</span>
                        <span className={`block text-[12px] mt-1 ${isSelected ? 'text-white/80' : 'text-[#6B7280]'}`}>
                          {option.description}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[13px] text-[#92400E] mt-3">
                  {t('agentSettings.aiProfileSearchContextSizeHelp')}
                </p>
              </div>

              {/* Advanced Azure Responses API web-search parameters — every
                  control below is opt-in; when off/empty, the corresponding
                  key is omitted entirely from the request instead of being
                  sent with a default value. */}
              <div className="mt-6 pt-5" style={{ borderTop: '1px solid #FDE68A' }}>
                <h5 className="text-[13px] font-semibold text-[#92400E] uppercase tracking-wide mb-4">
                  {t('agentSettings.aiProfileAdvancedWebSearchTitle')}
                </h5>

                {/* Force Web Search */}
                <div className="flex items-center justify-between mb-4 pb-4" style={{ borderBottom: '1px solid #FDE68A' }}>
                  <div>
                    <label className="text-[14px] font-medium text-[#374151]">
                      {t('agentSettings.aiProfileForceWebSearchLabel')}
                    </label>
                    <p className="text-[13px] text-[#92400E] mt-0.5">
                      {t('agentSettings.aiProfileForceWebSearchHelp')}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={formData[activeForceWebSearchField]}
                    disabled={!isSuperAdmin}
                    onClick={() => isSuperAdmin && setFormData(prev => ({ ...prev, [activeForceWebSearchField]: !prev[activeForceWebSearchField] }))}
                    className="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ml-4"
                    style={{ backgroundColor: formData[activeForceWebSearchField] ? aispaceColor : '#E5E7EB' }}
                  >
                    <span
                      className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
                      style={{ transform: formData[activeForceWebSearchField] ? 'translateX(22px)' : 'translateX(2px)' }}
                    />
                  </button>
                </div>

                {/* Enable Streaming */}
                <div className="flex items-center justify-between mb-4 pb-4" style={{ borderBottom: '1px solid #FDE68A' }}>
                  <div>
                    <label className="text-[14px] font-medium text-[#374151]">
                      {t('agentSettings.aiProfileStreamResponseLabel')}
                    </label>
                    <p className="text-[13px] text-[#92400E] mt-0.5">
                      {t('agentSettings.aiProfileStreamResponseHelp')}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={formData[activeStreamResponseField]}
                    disabled={!isSuperAdmin}
                    onClick={() => isSuperAdmin && setFormData(prev => ({ ...prev, [activeStreamResponseField]: !prev[activeStreamResponseField] }))}
                    className="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ml-4"
                    style={{ backgroundColor: formData[activeStreamResponseField] ? aispaceColor : '#E5E7EB' }}
                  >
                    <span
                      className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
                      style={{ transform: formData[activeStreamResponseField] ? 'translateX(22px)' : 'translateX(2px)' }}
                    />
                  </button>
                </div>

                {/* Include Search Sources (debug log) */}
                <div className="flex items-center justify-between mb-4 pb-4" style={{ borderBottom: '1px solid #FDE68A' }}>
                  <div>
                    <label className="text-[14px] font-medium text-[#374151]">
                      {t('agentSettings.aiProfileIncludeSearchSourcesLabel')}
                    </label>
                    <p className="text-[13px] text-[#92400E] mt-0.5">
                      {t('agentSettings.aiProfileIncludeSearchSourcesHelp')}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={formData[activeIncludeSearchSourcesField]}
                    disabled={!isSuperAdmin}
                    onClick={() => isSuperAdmin && setFormData(prev => ({ ...prev, [activeIncludeSearchSourcesField]: !prev[activeIncludeSearchSourcesField] }))}
                    className="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ml-4"
                    style={{ backgroundColor: formData[activeIncludeSearchSourcesField] ? aispaceColor : '#E5E7EB' }}
                  >
                    <span
                      className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
                      style={{ transform: formData[activeIncludeSearchSourcesField] ? 'translateX(22px)' : 'translateX(2px)' }}
                    />
                  </button>
                </div>

                {/* Show Reasoning — streams the model's live reasoning-summary
                    text (Azure's reasoning.summary) to the user while it
                    thinks, like Claude's own "Thinking" display. Only
                    produces content at Reasoning Effort Medium/High — shown
                    as a warning rather than hiding the toggle, since the
                    admin may turn this on before raising the effort level. */}
                <div className="flex items-center justify-between mb-2 pb-4" style={{ borderBottom: '1px solid #FDE68A' }}>
                  <div>
                    <label className="text-[14px] font-medium text-[#374151]">
                      {t('agentSettings.aiProfileShowReasoningLabel')}
                    </label>
                    <p className="text-[13px] text-[#92400E] mt-0.5">
                      {t('agentSettings.aiProfileShowReasoningHelp')}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={formData[activeShowReasoningField]}
                    disabled={!isSuperAdmin}
                    onClick={() => isSuperAdmin && setFormData(prev => ({ ...prev, [activeShowReasoningField]: !prev[activeShowReasoningField] }))}
                    className="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ml-4"
                    style={{ backgroundColor: formData[activeShowReasoningField] ? aispaceColor : '#E5E7EB' }}
                  >
                    <span
                      className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
                      style={{ transform: formData[activeShowReasoningField] ? 'translateX(22px)' : 'translateX(2px)' }}
                    />
                  </button>
                </div>
                {formData[activeShowReasoningField] && isGpt5Family && ["none", "low", ""].includes(formData[activeReasoningEffortField] || "none") && (
                  <p className="text-[13px] mb-4 px-3 py-2 rounded-lg" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                    {t('agentSettings.aiProfileShowReasoningEffortWarning')}
                  </p>
                )}
                {formData[activeShowReasoningField] && !formData[activeStreamResponseField] && (
                  <p className="text-[13px] mb-4 px-3 py-2 rounded-lg" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                    {t('agentSettings.aiProfileShowReasoningStreamWarning')}
                  </p>
                )}

                {/* Verbosity — toggle buttons, only when model is gpt-5.1. "Off"
                    (default) omits text.verbosity entirely instead of sending
                    a default value. */}
                <div className={`mb-4 ${isGpt5Family ? '' : 'hidden'}`}>
                  <label className="flex items-center gap-2 text-[14px] font-medium text-[#374151] mb-3">
                    {t('agentSettings.aiProfileVerbosityLabel')}
                    <Info size={16} className="text-[#9CA3AF]" />
                  </label>
                  <div className="grid grid-cols-4 gap-3">
                    {verbosityOptions.map((option) => {
                      const isSelected = (formData[activeVerbosityField] || '') === option.value;
                      return (
                        <button
                          key={option.value || 'off'}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, [activeVerbosityField]: option.value }))}
                          disabled={!isSuperAdmin}
                          className={`p-4 rounded-lg border-2 text-center transition-colors ${
                            isSelected
                              ? 'border-transparent text-white'
                              : 'border-[#FDE68A] text-[#374151] hover:border-[#D97706]'
                          } disabled:cursor-not-allowed bg-white`}
                          style={isSelected ? { backgroundColor: '#D97706' } : {}}
                        >
                          <span className="block text-[14px] font-medium">{option.label}</span>
                          <span className={`block text-[12px] mt-1 ${isSelected ? 'text-white/80' : 'text-[#6B7280]'}`}>
                            {option.description}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Max Tool Calls */}
                <div className="mb-4">
                  <label className="block text-[14px] font-medium text-[#374151] mb-2">
                    {t('agentSettings.aiProfileMaxToolCallsLabel')}
                  </label>
                  <input
                    type="number"
                    value={formData[activeMaxToolCallsField]}
                    onChange={(e) => setFormData(prev => ({ ...prev, [activeMaxToolCallsField]: e.target.value }))}
                    disabled={!isSuperAdmin}
                    placeholder={t('agentSettings.aiProfileMaxToolCallsPlaceholder')}
                    min="1"
                    max="50"
                    className="w-full px-4 py-3 text-[14px] border rounded-lg bg-white focus:outline-none transition-colors disabled:bg-[#FFFBEB] disabled:cursor-not-allowed"
                    style={{ borderColor: '#FDE68A' }}
                  />
                  <p className="text-[13px] text-[#92400E] mt-2">
                    {t('agentSettings.aiProfileMaxToolCallsHelp')}
                  </p>
                </div>

                {/* Azure Timeout / Max Retries — independently configurable
                    per mode (Web/Hybrid each have their own), same pattern as
                    every other field in this card. See routers/question.py's
                    generate_azure_web_search_response for the full reasoning
                    behind the 180s/0-retries defaults shown here. */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-[14px] font-medium text-[#374151] mb-2">
                      {t('agentSettings.aiProfileAzureTimeoutLabel')}
                    </label>
                    <input
                      type="number"
                      value={formData[activeAzureTimeoutField]}
                      onChange={(e) => setFormData(prev => ({ ...prev, [activeAzureTimeoutField]: e.target.value }))}
                      disabled={!isSuperAdmin}
                      placeholder="180"
                      min="10"
                      max="600"
                      className="w-full px-4 py-3 text-[14px] border rounded-lg bg-white focus:outline-none transition-colors disabled:bg-[#FFFBEB] disabled:cursor-not-allowed"
                      style={{ borderColor: '#FDE68A' }}
                    />
                    <p className="text-[13px] text-[#92400E] mt-2">
                      {t('agentSettings.aiProfileAzureTimeoutHelp')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-[14px] font-medium text-[#374151] mb-2">
                      {t('agentSettings.aiProfileAzureMaxRetriesLabel')}
                    </label>
                    <input
                      type="number"
                      value={formData[activeAzureMaxRetriesField]}
                      onChange={(e) => setFormData(prev => ({ ...prev, [activeAzureMaxRetriesField]: e.target.value }))}
                      disabled={!isSuperAdmin}
                      placeholder="0"
                      min="0"
                      max="3"
                      className="w-full px-4 py-3 text-[14px] border rounded-lg bg-white focus:outline-none transition-colors disabled:bg-[#FFFBEB] disabled:cursor-not-allowed"
                      style={{ borderColor: '#FDE68A' }}
                    />
                    <p className="text-[13px] text-[#92400E] mt-2">
                      {t('agentSettings.aiProfileAzureMaxRetriesHelp')}
                    </p>
                  </div>
                </div>

                {/* Allowed / Blocked Domains */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-[14px] font-medium text-[#374151] mb-2">
                      {t('agentSettings.aiProfileAllowedDomainsLabel')}
                    </label>
                    <textarea
                      value={formData[activeAllowedDomainsField]}
                      onChange={(e) => setFormData(prev => ({ ...prev, [activeAllowedDomainsField]: e.target.value }))}
                      disabled={!isSuperAdmin}
                      placeholder={t('agentSettings.aiProfileDomainsPlaceholder')}
                      rows={3}
                      className="w-full px-4 py-3 text-[14px] border rounded-lg bg-white focus:outline-none transition-colors disabled:bg-[#FFFBEB] disabled:cursor-not-allowed resize-none"
                      style={{ borderColor: '#FDE68A' }}
                    />
                  </div>
                  <div>
                    <label className="block text-[14px] font-medium text-[#374151] mb-2">
                      {t('agentSettings.aiProfileBlockedDomainsLabel')}
                    </label>
                    <textarea
                      value={formData[activeBlockedDomainsField]}
                      onChange={(e) => setFormData(prev => ({ ...prev, [activeBlockedDomainsField]: e.target.value }))}
                      disabled={!isSuperAdmin}
                      placeholder={t('agentSettings.aiProfileDomainsPlaceholder')}
                      rows={3}
                      className="w-full px-4 py-3 text-[14px] border rounded-lg bg-white focus:outline-none transition-colors disabled:bg-[#FFFBEB] disabled:cursor-not-allowed resize-none"
                      style={{ borderColor: '#FDE68A' }}
                    />
                  </div>
                </div>
                <p className="text-[13px] text-[#92400E] mb-5 -mt-2">
                  {t('agentSettings.aiProfileDomainsHelp')}
                </p>

                {/* Location Targeting */}
                <div className="flex items-center justify-between mb-4 pb-4" style={{ borderBottom: formData[activeUserLocationEnabledField] ? 'none' : '1px solid #FDE68A' }}>
                  <div>
                    <label className="text-[14px] font-medium text-[#374151]">
                      {t('agentSettings.aiProfileUserLocationLabel')}
                    </label>
                    <p className="text-[13px] text-[#92400E] mt-0.5">
                      {t('agentSettings.aiProfileUserLocationHelp')}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={formData[activeUserLocationEnabledField]}
                    disabled={!isSuperAdmin}
                    onClick={() => isSuperAdmin && setFormData(prev => ({ ...prev, [activeUserLocationEnabledField]: !prev[activeUserLocationEnabledField] }))}
                    className="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ml-4"
                    style={{ backgroundColor: formData[activeUserLocationEnabledField] ? aispaceColor : '#E5E7EB' }}
                  >
                    <span
                      className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
                      style={{ transform: formData[activeUserLocationEnabledField] ? 'translateX(22px)' : 'translateX(2px)' }}
                    />
                  </button>
                </div>

                {formData[activeUserLocationEnabledField] && (
                  <div className="grid grid-cols-2 gap-4 mb-2">
                    <div>
                      <label className="block text-[13px] font-medium text-[#374151] mb-1.5">
                        {t('agentSettings.aiProfileUserLocationCountryLabel')}
                      </label>
                      <input
                        type="text"
                        value={formData[activeUserLocationCountryField]}
                        onChange={(e) => setFormData(prev => ({ ...prev, [activeUserLocationCountryField]: e.target.value }))}
                        disabled={!isSuperAdmin}
                        placeholder={t('agentSettings.aiProfileUserLocationCountryPlaceholder')}
                        maxLength={2}
                        className="w-full px-4 py-2.5 text-[14px] border rounded-lg bg-white focus:outline-none transition-colors disabled:bg-[#FFFBEB] disabled:cursor-not-allowed"
                        style={{ borderColor: '#FDE68A' }}
                      />
                    </div>
                    <div>
                      <label className="block text-[13px] font-medium text-[#374151] mb-1.5">
                        {t('agentSettings.aiProfileUserLocationCityLabel')}
                      </label>
                      <input
                        type="text"
                        value={formData[activeUserLocationCityField]}
                        onChange={(e) => setFormData(prev => ({ ...prev, [activeUserLocationCityField]: e.target.value }))}
                        disabled={!isSuperAdmin}
                        placeholder={t('agentSettings.aiProfileUserLocationCityPlaceholder')}
                        className="w-full px-4 py-2.5 text-[14px] border rounded-lg bg-white focus:outline-none transition-colors disabled:bg-[#FFFBEB] disabled:cursor-not-allowed"
                        style={{ borderColor: '#FDE68A' }}
                      />
                    </div>
                    <div>
                      <label className="block text-[13px] font-medium text-[#374151] mb-1.5">
                        {t('agentSettings.aiProfileUserLocationRegionLabel')}
                      </label>
                      <input
                        type="text"
                        value={formData[activeUserLocationRegionField]}
                        onChange={(e) => setFormData(prev => ({ ...prev, [activeUserLocationRegionField]: e.target.value }))}
                        disabled={!isSuperAdmin}
                        placeholder={t('agentSettings.aiProfileUserLocationRegionPlaceholder')}
                        className="w-full px-4 py-2.5 text-[14px] border rounded-lg bg-white focus:outline-none transition-colors disabled:bg-[#FFFBEB] disabled:cursor-not-allowed"
                        style={{ borderColor: '#FDE68A' }}
                      />
                    </div>
                    <div>
                      <label className="block text-[13px] font-medium text-[#374151] mb-1.5">
                        {t('agentSettings.aiProfileUserLocationTimezoneLabel')}
                      </label>
                      <input
                        type="text"
                        value={formData[activeUserLocationTimezoneField]}
                        onChange={(e) => setFormData(prev => ({ ...prev, [activeUserLocationTimezoneField]: e.target.value }))}
                        disabled={!isSuperAdmin}
                        placeholder={t('agentSettings.aiProfileUserLocationTimezonePlaceholder')}
                        className="w-full px-4 py-2.5 text-[14px] border rounded-lg bg-white focus:outline-none transition-colors disabled:bg-[#FFFBEB] disabled:cursor-not-allowed"
                        style={{ borderColor: '#FDE68A' }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default SystemPrompts;
